import { useSocketContext } from "@/contexts/socketioContext";
import { createContentCardDate } from "@/utils/momentManipulate";
import {
  useGetQueryData,
  useSubscribeToQueryData,
} from "@/utils/react-query-hooks/general";
import {
  useFailedImportRowsCount,
  useDownloadFailedImportRowsCsv,
} from "@/utils/react-query-hooks/failedImportRows";
import { BASE_BULLMQ_CONFIG } from "@okf/ootils";
import { buildSyntheticSegByImportIdJobs } from "./QueueJobsProgressFeedbackTester";
import {
  ButtonGhostCompact,
  colors2,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION_DF,
  UI_CAPTION_SEMIBOLD_DF,
  LoaderCircleV2,
  icons,
  UI_BODY_SEMIBOLD_SM,
  TooltipV2,
} from "oolib";
import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { RotatingCaret } from "../generalUI/RotatingCaret";
import { useNavigate } from "react-router-dom";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import styled from "styled-components";
import { ImportProgressItem, ImportStatusPill } from "./comps/ImportProgressItem";
import { useImportEta } from "./useImportEta";

// ButtonGhostCompact renders its label as a <p> child of a flex <button>.
// Default flex items use min-width: auto, so the <p> refuses to shrink and
// long CTA text overflows the box. This wrapper forces the inner <p> to
// shrink + ellipsis.
const EllipsisCtaButton = styled(ButtonGhostCompact)`
  max-width: 100%;
  min-width: 0;
  & > p {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

//order matters here, thats the order in which we show the
//progress bars
export const GET_QUEUE_DISPLAY_NAMES = (_Theme, tplTitle) => {
  // status-first phrasing so when the bar ellipses, the title (variable, lower
  // priority) gets truncated and not the actual status. when no title resolved,
  // we drop the dash suffix entirely instead of reading "Imported — Data".
  const titleSuffix = tplTitle ? ` — ${tplTitle}` : "";
  return ({
  [BASE_BULLMQ_CONFIG.DIRECT_DATA_IMPORT_QUEUE.id]: {
    // loadingLabel: "Importing data...",
    // completedLabel: "Data imported",
    // icon: 'DownloadSimple',
    runIdx: 0,
    loading: {
      icon: "DownloadSimple",
      label: tplTitle ? `Importing ${tplTitle}` : `Importing data`,
      color: _Theme?.colors?.onSecondary,
    },
    fullyCompleted: {
      icon: "CheckCircle",
      label: `Imported & AI Ready!${titleSuffix}`,
      color: colors2.green,
    },
    completed: {
      icon: "CheckCircle",
      label: `Imported Successfully${titleSuffix}`,
      shortLabel: `Imported${titleSuffix}`,
      color: colors2.green,
      secondaryLabel: {
        //showed when data is imported, but ai is still processing
        text: "Setting up AI",
        loader: true,
        color: _Theme?.colors?.onSecondary,
      },
    },
    expandContent: {
      completed: {
        infoText: tplTitle
          ? `You can start exploring your ${tplTitle} while the rest is being imported.`
          : `You can start exploring your data while the rest is being imported.`,
        cta: {
          text: tplTitle ? `View Imported ${tplTitle}` : `View Imported Data`,
          icon: "FolderSimple",
          onClick: ({ navigate, contentType }) => {
            navigate(`/datasets/${contentType}`);
          },
        },
      },
    },
  },
  [BASE_BULLMQ_CONFIG.CREATE_CHUNKS_QUEUE.id]: {
    runIdx: 1,
    // loadingLabel: "Preparing data for AI (1 of 3)...",
    // completedLabel: "Prep for AI complete! (1 of 3)",
  },
  [BASE_BULLMQ_CONFIG.CONTENT_ENHANCE_AND_EMBED_QUEUE.id]: {
    runIdx: 2,
    // loadingLabel: "Refining data for AI (2 of 3)...",
    // completedLabel: "Refinement for AI complete! (2 of 3)",
  },
  [BASE_BULLMQ_CONFIG.CHUNKS_ELASTIC_SYNC_QUEUE.id]: {
    runIdx: 3,
    // loadingLabel: "Setting up Ask AI & Summaries (3 of 3)...",
    // completedLabel: "Ask AI & Summaries ready to use!",
    expandContent: {
      loading: {
        // loader: true,
        // icon: 'DownloadSimple',
        icon: "Chat",
        label: "Enabling Ask AI & Summaries",
        color: _Theme?.colors?.onSecondary,
      },
      completed: {
        icon: "CheckCircle",
        label: "Ask AI & Summaries Enabled",
        color: colors2.green,
        cta: {
          text: "Go to Ask AI",
          icon: "Chat",
          onClick: ({ navigate }) => {
            navigate("/ai-chat");
          },
        },
      },
    },
  },
  });
};

const QueueProgressBar = ({
  color,
  icon,
  progressBarLabel,
  // optional override for the on-hover tooltip text. defaults to
  // progressBarLabel when not provided.
  tooltipText,
  completedCount,
  totalItems,
  completedPercent,
  failedPercent = 0,
  // suppresses the right-side `X%` text. used by callers in failure-settled
  // states where completedPercent < 100 but we still want a "done"-style
  // header without a number.
  hidePercent = false,

  expanded,
  onToggleExpand,
  showExpandButton = true,
  showCloseButton = false,
  handleClose,
  useLoader = false,
  secondaryLabel,
  onSecondaryColor,
  secondaryContainerColor,
}) => {
  const genIcon = ({ icon, size, color, weight }) => {
    const Icon = icons[icon];
    if (!Icon) return null;
    return <Icon size={size} color={color} weight={weight} />;
  };

  return (
    <div style={{ width: "100%" }}>
      <button
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "6px",
          border: "none",
          background: "unset",
          cursor: showExpandButton ? "pointer" : "default",
        }}
        onClick={onToggleExpand}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            // minWidth:0 + flex:1 lets the inner label ellipsis instead of
            // pushing the right-side (percent + caret) out of the box
            minWidth: 0,
            flex: 1,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
              // hug content (don't grow) but allow shrink so the inner label
              // can ellipsis when long. Without this, a short label like
              // "Imported — Articles" stretches and pushes the secondary
              // ("Setting up AI") to the far right of the card.
              flex: "0 1 auto",
            }}
          >
            <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
              {useLoader ? (
                <LoaderCircleV2 S color={color} />
              ) : (
                icon && genIcon({ icon, color, size: 20, weight: "bold" })
              )}
            </div>
            {/* the outer div is the flex item (TooltipV2's wrapper doesn't
                accept flex styling, and display:contents had no box for
                floating-ui to anchor to, so the tooltip floated to 0,0).
                textAlign:left is needed because the parent <button> defaults
                to text-align: center, which would otherwise center the label
                in the available space. */}
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <TooltipV2 text={tooltipText || progressBarLabel} placement="top" wrapperDisplay="block">
                <UI_BODY_SEMIBOLD_SM_DF
                  style={{
                    color,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {progressBarLabel}
                </UI_BODY_SEMIBOLD_SM_DF>
              </TooltipV2>
            </div>
          </div>
          {secondaryLabel && (
            <Fragment>
              <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey10, flex: "0 0 auto" }}>
                |
              </UI_BODY_SEMIBOLD_SM_DF>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  // pin "Setting up AI" at its natural width — let the main
                  // (variable-length tplTitle) label do the shrinking
                  flex: "0 0 max-content",
                }}
              >
                <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
                  {secondaryLabel.loader ? (
                    <LoaderCircleV2
                      S
                      color={secondaryLabel.color}
                    />
                  ) : (
                    icon &&
                    genIcon({
                      icon: secondaryLabel.icon,
                      color: secondaryLabel.color,
                      size: 20,
                      weight: "bold",
                    })
                  )}
                </div>
                <UI_BODY_SEMIBOLD_SM_DF
                  style={{
                    color: secondaryLabel.color,
                    whiteSpace: "nowrap",
                  }}
                >
                  {secondaryLabel.text}
                </UI_BODY_SEMIBOLD_SM_DF>
              </div>
            </Fragment>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: onSecondaryColor,
            flex: "0 0 auto",
          }}
        >
          {!hidePercent && completedPercent !== 100 &&
            (completedCount !== undefined && totalItems !== undefined ? (
              <UI_BODY_SEMIBOLD_SM_DF>{`${completedCount} / ${totalItems}`}</UI_BODY_SEMIBOLD_SM_DF>
            ) : (
              <UI_BODY_SEMIBOLD_SM_DF>{`${completedPercent}%`}</UI_BODY_SEMIBOLD_SM_DF>
            ))}
          {showExpandButton && (
            <RotatingCaret
              color={colors2.grey80}
              iconSize={16}
              position={expanded ? "up" : "down"}
            />
          )}
          {showCloseButton && (
            <ButtonGhostCompact
              onClick={handleClose}
              icon="X"
            />
          )}
        </div>
      </button>
      {completedPercent !== undefined &&
        (completedPercent !== 100 || failedPercent > 0) && (
        <div
          style={{
            display: "flex",
            width: "100%",
            marginTop: "8px",
            gap: "2px",
          }}
        >
          {/* the original blue progress bar (track + fill), now treated as
              one flex unit. when failures exist it shrinks to make room
              for the red bar sibling. */}
          <div
            style={{
              flex: failedPercent > 0 ? `0 0 ${100 - failedPercent}%` : "1 1 auto",
              position: "relative",
              height: "8px",
              backgroundColor: secondaryContainerColor,
              borderRadius: "14px",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: completedPercent + "%",
                backgroundColor: onSecondaryColor,
                borderRadius: "14px",
              }}
            />
          </div>
          {failedPercent > 0 && (
            <div
              style={{
                flex: `0 0 ${failedPercent}%`,
                height: "8px",
                backgroundColor: colors2.red,
                borderRadius: "14px",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

const getQueueDataFromQueueId = (queueId, queues) => {
  const queueName = Object.keys(queues).find((name) =>
    name.startsWith(queueId)
  );

  if (!queueName) return undefined;

  return queues[queueName];
};

/**
 * the final step that defines AI readiness for the import:
 * ask AI & summarization ready
 */
const finalAIQueueSteps = [
  BASE_BULLMQ_CONFIG.CHUNKS_ELASTIC_SYNC_QUEUE.id,
];

// Outer card + header. The header today is the single import's main progress
// bar (passed in by the parent); when the card goes multi-import it becomes an
// independent aggregate row here (e.g. "Importing your data — 2/4 done").
const ProgressFeedbackShell = ({ header, children }) => (
  // Fixed positioning + bottom-right anchoring lives on the parent stack
  // container in App.jsx, so this just emits the card as a flex child.
  <div
    style={{
      minWidth: "350px",
      // no maxWidth — the card hugs its content (rows are a fixed 450px)
      boxShadow: `0px 4px 20px 0px ${colors2.grey15}`,
      border: `1px solid ${colors2.grey10}`,
      borderRadius: "16px",
      background: colors2.white,
    }}
  >
    {header}
    {children}
  </div>
);

/**
 * `testMode` (optional): when provided, drives the component into a synthetic
 * state without needing a real import. Just type it directly as a prop:
 *
 *   <QueueJobsProgressFeedback
 *     testMode={{ contentType: "anything", state: "imported", tplTitle: "Articles" }}
 *   />
 *
 * Shape:
 *   - contentType: any string (need NOT be a real tpl id — it just becomes
 *     the navigate param for the CTAs)
 *   - state: "importing" | "imported" | "aiGettingReady" | "aiReady"
 *   - tplTitle (optional): free-form string used in the labels. Overrides the
 *     real tpl-cache lookup when set; falls back to the lookup, then to "Data".
 *
 * In test mode, the localStorage close-suppression is bypassed so toggling
 * states / re-running works freely.
 */
export const QueueJobsProgressFeedback = ({ testMode } = {}) => {
  const { segByImportIdJobs: realSegByImportIdJobs } = useSocketContext();
  const { _Theme } = useGetQueryData("platformConfigs");

  const navigate = useNavigate();

  const onSecondaryColor = _Theme?.colors?.onSecondary;
  const secondaryContainerColor = _Theme?.colors?.secondaryContainer;

  const segByImportIdJobs = testMode
    ? buildSyntheticSegByImportIdJobs(testMode)
    : realSegByImportIdJobs;

  const [expanded, setExpanded] = useState(true);
  // QUEUE_DISPLAY_NAMES moved below — it depends on tplTitle, which we derive
  // from `contentType` extracted in the useMemo further down.

  const {
    importIdKey,
    latestImportData,
    queueData,
    importId,
    importTotalCount,
    activityDate,
    queues,
    contentType,
    allAIProcessingQueuesAreComplete,
    dataImportComplete,
    dataImportSettled,
    importAndProcessingComplete,
  } = useMemo(() => {
    if(!segByImportIdJobs || Object.keys(segByImportIdJobs).length === 0) return {}
    const importIdKey = Object.keys(segByImportIdJobs)[0];
    //just render the first for now. ofcourse all this is going to change drastically
    const latestImportData = segByImportIdJobs[importIdKey];

    const { importId, importTotalCount, activityDate, queues, contentType } =
      latestImportData;

    const queueData = getQueueDataFromQueueId(
      BASE_BULLMQ_CONFIG.DIRECT_DATA_IMPORT_QUEUE.id,
      queues
    );

    

    // dataImportComplete = strict 100% success. dataImportSettled = no jobs
    // pending (everything in completed OR failed). The "Setting up AI" /
    // "fully done" UI gates use `Settled` so failure cases still get the
    // right downstream state.
    const dataImportComplete = queueData?.completed.percent === 100;
    const dataImportSettled =
      (queueData?.completed?.percent || 0) +
        (queueData?.failed?.percent || 0) >=
      100 - 0.01;

    const allAIProcessingQueuesAreComplete = finalAIQueueSteps.every(
      (queueId) => {
        const _qD = getQueueDataFromQueueId(queueId, queues);

        /**
         * this condition because, sometimes, on some contenttypes,
         * AI categorization may not be enabled at all via the direct
         * import interface. In such cases, it's queue data will be undefined,
         * and we simply wanna ignore it, since it shouldnt be taken into
         * consideration when deciding if AI processing is complete or not.
         * 
         * BUT if the queue is the chunks elastic sync queue and it doesnt exist,
         * it means its processing hasnt started yet.. so dont simply say 'AI Ready'. its not.
         */

        
        if (!_qD){
          if(queueId === BASE_BULLMQ_CONFIG.CHUNKS_ELASTIC_SYNC_QUEUE.id){
            return false
          }else{
            return true
          }
        }
        return _qD?.actualPercentCompleted === 100;
      }
    );

    const importAndProcessingComplete =
      dataImportSettled && allAIProcessingQueuesAreComplete;


    return {
      importIdKey,
      latestImportData,
      queueData,
      importId,
      importTotalCount,
      activityDate,
      queues,
      contentType,
      allAIProcessingQueuesAreComplete,
      dataImportComplete,
      dataImportSettled,
      importAndProcessingComplete,
    };
  }, [segByImportIdJobs]);

  // This popup mounts when the import STARTS, but the tpl only reaches the
  // cache when the worker commits it — the socket `tplCreated` push lands it
  // in ["tpl", contentType] via updateAllTplsCache, seconds later. So the
  // lookup has to SUBSCRIBE to the cache rather than read it once: the old
  // one-shot getQueryData ran before the push arrived and stayed frozen on the
  // fallback forever, which is why rows read "Data" instead of the dataset
  // name. Neither subscription fetches.
  // ["tci","tpls"] is the second source: when the push is missed, the Datasets
  // page's own getAllTpls fetch fills that array but not the singular key.
  // Last comes latestImportData.tplTitle — the title typed at import time,
  // stamped onto the job's sourceMetadata by DirectDataImportProducer. It's the
  // only one available before the tpl exists at all, so it covers the gap
  // between starting a first-time import and batch 0 committing the tpl. It
  // ranks last because the committed tpl is authoritative once it lands.
  // testMode.tplTitle takes priority so we can test with any free-form string.
  const { data: tplFromCache } = useSubscribeToQueryData(["tpl", contentType]);
  const { data: allTplsFromCache } = useSubscribeToQueryData(["tci", "tpls"]);
  const tplTitle =
    testMode?.tplTitle ??
    tplFromCache?.general?.content?.title ??
    allTplsFromCache?.find((t) => t.kp_content_type === contentType)?.general
      ?.content?.title ??
    latestImportData?.tplTitle;

  const QUEUE_DISPLAY_NAMES = useMemo(() => {
    return GET_QUEUE_DISPLAY_NAMES(_Theme, tplTitle);
  }, [_Theme, tplTitle]);

  // Rows the BE validator rejected and skipped (row-level quarantine). Fetched
  // once the data import settles — counts only exist after batches run. This is
  // a different signal from `failed` above: those are whole failed BullMQ jobs,
  // these are individual rows omitted from otherwise-successful batches.
  const { data: skippedRowsCount = 0 } = useFailedImportRowsCount(importId, {
    enabled: !testMode && !!dataImportSettled,
  });
  const downloadFailedImportRowsCsv = useDownloadFailedImportRowsCsv();

  // hooks can't sit after the early returns, hence not next to the row derivation
  const totalRowsForEta = importTotalCount || queueData?.totalItems || 0;
  const etaText = useImportEta({
    importId,
    completedCount: Math.round(
      ((queueData?.completed?.percent || 0) / 100) * totalRowsForEta
    ),
    totalCount: totalRowsForEta,
    active: !dataImportSettled && totalRowsForEta > 0,
  });

  //if an explicit import id exists, and we havnt blacklisted it in localstorage, then show
  //this lightbox, else default to not showing.
  //(in testMode, bypass the localStorage check so the dev tester can toggle states freely)
  const [showThisLightbox, setShowThisLightbox] = useState(
    importId && (testMode || importId !== localStorage.getItem('CLOSED_jobsProgressFeedbackImportId'))
    ? true : false
  )


  //if explicit import id exists and we havnt blacklisted it in localstorage, then, in
  //case the lightbox isn't already showing, then show it.
  useEffect(() => {
    if(
      importId &&
      (testMode || importId !== localStorage.getItem('CLOSED_jobsProgressFeedbackImportId')) &&
      !showThisLightbox
    ){
      setShowThisLightbox(true)
    }
  },[importId, showThisLightbox, testMode])

  // TEMPORARY: restores the dismiss affordance that used to live on the header's
  // QueueProgressBar, until the designed header grows its own close control.
  // Without it the card is undismissable — it renders for as long as the
  // import's jobs sit in Redis, which outlives the import itself.
  const handleClose = (e) => {
    // the whole header toggles expand on click; don't also toggle when closing
    e.stopPropagation();
    if (!testMode) {
      localStorage.setItem("CLOSED_jobsProgressFeedbackImportId", importId);
    }
    setShowThisLightbox(false);
  };


  if (!showThisLightbox || !segByImportIdJobs || Object.keys(segByImportIdJobs).length === 0) {
    return null;
  }

  // No direct-import queue entry for this importId (e.g. import jobs already
  // evicted from Redis while AI queues linger) — genVisibleElement would render
  // nothing, leaving an empty card shell. Render nothing at all instead.
  if (!queueData) {
    return null;
  }

  

  const genVisibleElement = () => {
    const queueId = BASE_BULLMQ_CONFIG.DIRECT_DATA_IMPORT_QUEUE.id;

    if (!queueData) return null;

    const failedCount = queueData?.failed?.count || 0;
    const hasFailures = failedCount > 0;
    const completedPctDisplay = Math.round(queueData?.completed?.percent || 0);
    const failedPctDisplay = Math.round(queueData?.failed?.percent || 0);
    const titleSuffix = tplTitle ? ` — ${tplTitle}` : "";
    // hover tooltip carries the percentage breakdown when there are failures
    const failureTooltip = `${completedPctDisplay}% Imported, ${failedPctDisplay}% Failed${titleSuffix}`;

    let progressBarLabel, secondaryLabel, icon, color, tooltipText;

    if (dataImportSettled && !allAIProcessingQueuesAreComplete) {
      // imported (data settled, AI still processing)
      if (hasFailures) {
        icon = "Warning";
        color = colors2.redText;
        progressBarLabel = `Imported, with failures${titleSuffix}`;
        tooltipText = failureTooltip;
        secondaryLabel = expanded
          ? undefined
          : QUEUE_DISPLAY_NAMES[queueId]?.completed?.secondaryLabel;
      } else {
        progressBarLabel = expanded
          ? QUEUE_DISPLAY_NAMES[queueId]?.completed?.label
          : QUEUE_DISPLAY_NAMES[queueId]?.completed?.shortLabel;
        secondaryLabel = expanded
          ? undefined
          : QUEUE_DISPLAY_NAMES[queueId]?.completed?.secondaryLabel;
        icon = QUEUE_DISPLAY_NAMES[queueId]?.completed?.icon;
        color = QUEUE_DISPLAY_NAMES[queueId]?.completed?.color;
      }
    } else if (allAIProcessingQueuesAreComplete) {
      // fully done (data + AI)
      if (hasFailures) {
        icon = "Warning";
        color = colors2.redText;
        progressBarLabel = `Imported & AI Ready, with failures${titleSuffix}`;
        tooltipText = failureTooltip;
      } else {
        progressBarLabel = QUEUE_DISPLAY_NAMES[queueId]?.fullyCompleted?.label;
        icon = QUEUE_DISPLAY_NAMES[queueId]?.fullyCompleted?.icon;
        color = QUEUE_DISPLAY_NAMES[queueId]?.fullyCompleted?.color;
      }
    } else {
      // loading (importing — keep as-is per user; failure shown via the red
      // bar segment only, no top-text changes)
      progressBarLabel = QUEUE_DISPLAY_NAMES[queueId]?.loading?.label;
      icon = QUEUE_DISPLAY_NAMES[queueId]?.loading?.icon;
      color = QUEUE_DISPLAY_NAMES[queueId]?.loading?.color;
    }

    return (
      <div>
        <QueueProgressBar
          color={color}
          key={queueId}
          icon={icon}
          progressBarLabel={progressBarLabel}
          tooltipText={tooltipText}
          // hide the right-side `X%` text in failure-settled states — the
          // header already implies "done" via "Imported, with failures…"
          hidePercent={hasFailures && dataImportSettled}
          // completedCount={queueData.completed?.count}
          // totalItems={queueData.totalItems}
          completedPercent={queueData?.completed.percent}
          failedPercent={queueData?.failed?.percent || 0}
          showExpandButton={importAndProcessingComplete ? false : true}
          showCloseButton={importAndProcessingComplete ? true : false}
          handleClose={() => {
            if (!testMode) {
              localStorage.setItem('CLOSED_jobsProgressFeedbackImportId', importId)
            }
            setShowThisLightbox(false)
          }}
          expanded={importAndProcessingComplete ? false : expanded}
          onToggleExpand={() =>
            importAndProcessingComplete ? null : setExpanded((prev) => !prev)
          }
          secondaryLabel={secondaryLabel}
          onSecondaryColor={onSecondaryColor}
          secondaryContainerColor={secondaryContainerColor}
        />
        {importAndProcessingComplete && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "12px",
            }}
          >
            {[
              BASE_BULLMQ_CONFIG.DIRECT_DATA_IMPORT_QUEUE.id,
              ...finalAIQueueSteps,
            ].map((queueId) => {
              const { expandContent } = QUEUE_DISPLAY_NAMES[queueId];
              return (
                <EllipsisCtaButton
                  style={{
                    border: `1px solid ${colors2.grey10}`,
                  }}
                  onClick={() =>
                    expandContent?.completed?.cta.onClick({
                      navigate,
                      contentType,
                    })
                  }
                  icon={expandContent?.completed?.cta.icon}
                >
                  {expandContent?.completed?.cta.text}
                </EllipsisCtaButton>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Per-import row (the design's repeatable block), derived from the same
  // flags the header uses. Lives in the content area; header changes come later.
  // Two distinct failure signals feed "importedWithFailures": whole failed
  // BullMQ jobs (queueData.failed) AND validator-skipped rows
  // (skippedRowsCount) from otherwise-successful batches.
  const failedCount = queueData?.failed?.count || 0;
  const completedCount = queueData?.completed?.count || 0;

  // Row displays ROW counts, not BullMQ job counts (5 batches ≠ 5 rows).
  // importTotalCount is the expected row total; mid-flight completed rows are
  // estimated from job percent (batches are uniform), and once settled the
  // skipped-rows count makes them exact.
  const totalRows = importTotalCount || queueData?.totalItems || 0;
  const failedRows =
    skippedRowsCount +
    Math.round(((queueData?.failed?.percent || 0) / 100) * totalRows);
  const completedRows = dataImportSettled
    ? Math.max(totalRows - failedRows, 0)
    : Math.round(((queueData?.completed?.percent || 0) / 100) * totalRows);
  const rowStatus = !dataImportSettled
    ? "importing"
    : failedCount === 0 && skippedRowsCount === 0
    ? "imported"
    : completedCount === 0
    ? "failed"
    : "importedWithFailures";
  const displayTitle = tplTitle || "Data";
  const rowTitle = {
    importing: `Importing “${displayTitle}”`,
    imported: `“${displayTitle}” Imported`,
    failed: `“${displayTitle}” Import Failed`,
    importedWithFailures: `“${displayTitle}” Imported with failures`,
  }[rowStatus];

  return (
    <ProgressFeedbackShell
      key={importIdKey}
      header={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "6px",
            padding: "16px",
            cursor: "pointer",
          }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? (
            <UI_BODY_SEMIBOLD_SM_DF>Data Import</UI_BODY_SEMIBOLD_SM_DF>
          ) : (
            // collapsed: no title — each import compresses into a mini status
            // pill (one import today; maps over all of them when we go multi)
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <ImportStatusPill
                status={rowStatus}
                fraction={totalRows ? completedRows / totalRows : 0}
              />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <RotatingCaret
              color={colors2.grey80}
              iconSize={16}
              position={expanded ? "up" : "down"}
            />
            {/* Deliberately ungated. The old header gated this on
                importAndProcessingComplete, which is only true once the
                chunks-elastic-sync queue exists AND hits 100% — and that queue
                is never created for content types with chunking disabled
                (CreateChunksProducer bails: "Chunking is not enabled on
                contentType"). So on those imports the flag stays false forever
                and the close button could never appear. Matches
                AutoAnnotateProgressFeedback, which is always dismissable. */}
            <ButtonGhostCompact onClick={handleClose} icon="X" />
          </div>
        </div>
      }
    >
      {expanded && (
        <div
          style={{
            padding: "16px",
            borderTop: `1px solid ${colors2.grey10}`,
          }}
        >
          <ImportProgressItem
            status={rowStatus}
            title={rowTitle}
            completedCount={completedRows}
            totalCount={totalRows}
            failedCount={failedRows}
            subText={
              rowStatus === "importedWithFailures"
                ? `Failed to import ${failedRows} row${failedRows === 1 ? "" : "s"}`
                : rowStatus === "importing"
                ? etaText
                : undefined
            }
            onView={
              rowStatus === "imported" && contentType
                ? () => navigate(`/datasets/${contentType}`)
                : undefined
            }
            onDownloadFailedRows={
              skippedRowsCount > 0
                ? () => downloadFailedImportRowsCsv.mutate(importId)
                : undefined
            }
            ctaDisabled={downloadFailedImportRowsCsv.isLoading}
          />
        </div>
      )}
    </ProgressFeedbackShell>
  );
};
